// Client-side helpers for DT photo compression, GPS extraction and offline draft persistence.

const MAX_DIM = 1600;
const TARGET_BYTES = 500 * 1024;
const QUALITIES = [0.86, 0.8, 0.74, 0.68, 0.62];

export const DRAFT_KEY = "pps_dt_new_draft_v1";

export const newLocalId = () =>
  (window.crypto?.randomUUID?.() || `${Date.now()}-${Math.random().toString(36).slice(2)}`);

const loadBitmap = async (file) => {
  if (window.createImageBitmap) {
    try { return await createImageBitmap(file, { imageOrientation: "from-image" }); } catch { /* fallback below */ }
  }
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => { URL.revokeObjectURL(url); resolve(img); };
    img.onerror = (e) => { URL.revokeObjectURL(url); reject(e); };
    img.src = url;
  });
};

const toBlob = (canvas, q) => new Promise((res) => canvas.toBlob(res, "image/jpeg", q));

export async function compressImage(file) {
  const bmp = await loadBitmap(file);
  const w = bmp.width, h = bmp.height;
  const isJpeg = /jpe?g$/i.test(file.type);
  if (isJpeg && file.size <= TARGET_BYTES && Math.max(w, h) <= MAX_DIM) return file;
  const scale = Math.min(1, MAX_DIM / Math.max(w, h));
  const canvas = document.createElement("canvas");
  canvas.width = Math.round(w * scale);
  canvas.height = Math.round(h * scale);
  const ctx = canvas.getContext("2d");
  ctx.imageSmoothingQuality = "high";
  ctx.drawImage(bmp, 0, 0, canvas.width, canvas.height);
  bmp.close?.();
  let blob = null;
  for (const q of QUALITIES) {
    blob = await toBlob(canvas, q);
    if (blob && blob.size <= TARGET_BYTES) break;
  }
  if (!blob) return file;
  if (blob.size >= file.size && isJpeg) return file;
  const base = (file.name || "photo").replace(/\.[^.]+$/, "");
  return new File([blob], `${base}.jpg`, { type: "image/jpeg", lastModified: Date.now() });
}

export const currentLocation = () => new Promise((resolve) => {
  if (!("geolocation" in navigator)) return resolve(null);
  navigator.geolocation.getCurrentPosition(
    p => resolve({ latitude: +p.coords.latitude.toFixed(6), longitude: +p.coords.longitude.toFixed(6) }),
    () => resolve(null),
    { enableHighAccuracy: true, timeout: 6000, maximumAge: 60000 }
  );
});

// Minimal JPEG EXIF GPS reader (must run on the ORIGINAL file; canvas output has no EXIF).
export async function extractExifGPS(file) {
  try {
    const dv = new DataView(await file.slice(0, 256 * 1024).arrayBuffer());
    if (dv.getUint16(0) !== 0xFFD8) return null;
    let off = 2;
    while (off + 4 < dv.byteLength) {
      const marker = dv.getUint16(off); off += 2;
      const size = dv.getUint16(off); off += 2;
      if (marker === 0xFFE1) {
        const header = String.fromCharCode(dv.getUint8(off), dv.getUint8(off + 1), dv.getUint8(off + 2), dv.getUint8(off + 3));
        if (header !== "Exif") { off += size - 2; continue; }
        const tiff = off + 6;
        const le = dv.getUint16(tiff) === 0x4949;
        const ifd0 = tiff + dv.getUint32(tiff + 4, le);
        const n = dv.getUint16(ifd0, le);
        let gpsOff = null;
        for (let i = 0; i < n; i++) {
          const e = ifd0 + 2 + i * 12;
          if (dv.getUint16(e, le) === 0x8825) { gpsOff = tiff + dv.getUint32(e + 8, le); break; }
        }
        if (!gpsOff) return null;
        const gN = dv.getUint16(gpsOff, le);
        const g = {};
        for (let i = 0; i < gN; i++) {
          const e = gpsOff + 2 + i * 12;
          const tag = dv.getUint16(e, le), type = dv.getUint16(e + 2, le), count = dv.getUint32(e + 4, le);
          const vo = tiff + dv.getUint32(e + 8, le);
          if (tag === 1 || tag === 3) g[tag === 1 ? "latRef" : "lonRef"] = String.fromCharCode(dv.getUint8(e + 8));
          if ((tag === 2 || tag === 4) && type === 5 && count === 3) {
            const r = (o) => dv.getUint32(o, le) / dv.getUint32(o + 4, le);
            g[tag === 2 ? "lat" : "lon"] = r(vo) + r(vo + 8) / 60 + r(vo + 16) / 3600;
          }
        }
        if (g.lat == null || g.lon == null) return null;
        const lat = g.latRef === "S" ? -g.lat : g.lat;
        const lon = g.lonRef === "W" ? -g.lon : g.lon;
        return { latitude: +lat.toFixed(6), longitude: +lon.toFixed(6), source: "exif" };
      }
      if ((marker & 0xFF00) !== 0xFF00) break;
      off += size - 2;
    }
  } catch { /* no EXIF */ }
  return null;
}

// ---- IndexedDB blob store for not-yet-uploaded photos ----
const DB_NAME = "pps_dt_offline";
const STORE = "photos";
const openDb = () => new Promise((resolve, reject) => {
  const req = indexedDB.open(DB_NAME, 1);
  req.onupgradeneeded = () => req.result.createObjectStore(STORE);
  req.onsuccess = () => resolve(req.result);
  req.onerror = () => reject(req.error);
});
const tx = async (mode, fn) => {
  const db = await openDb();
  return new Promise((resolve, reject) => {
    const t = db.transaction(STORE, mode);
    const r = fn(t.objectStore(STORE));
    t.oncomplete = () => resolve(r?.result);
    t.onerror = () => reject(t.error);
  });
};
export const blobStore = {
  put: (id, blob) => tx("readwrite", s => s.put(blob, id)).catch(() => null),
  get: (id) => tx("readonly", s => s.get(id)).catch(() => null),
  del: (id) => tx("readwrite", s => s.delete(id)).catch(() => null),
  clear: () => tx("readwrite", s => s.clear()).catch(() => null),
};

export const stripPhoto = (p) => ({
  file_id: p.file_id, url: p.url, category: p.category, filename: p.filename,
  uploaded_at: p.uploaded_at, latitude: p.latitude ?? null, longitude: p.longitude ?? null,
  gps_source: p.gps_source ?? null,
});

export const isPhotoReady = (p) => !p.local_id || p.status === "uploaded";
